import { BarChart } from '@ovComponents/2-component/barChart/barChart';
import { formatCompactNumber } from '@utils/formatting';
import { formatMoneyValue, formatPercentValue } from '@utils/moneyFormatting';
import type { ChartOptions, Plugin } from 'chart.js';
import { Box, Skeleton, Typography, useTheme } from 'hephaestus/web';
import { isNull } from 'lodash-es';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { AumExplorerGroupByTypes } from './aumExplorer';

export const AumExplorerBar = ({
  loading,
  sData,
  totalValue,
  colors,
}: {
  loading: boolean;
  sData: any[];
  totalValue: number;
  groupBy: AumExplorerGroupByTypes;
  colors: (`#${string}` | undefined)[];
}) => {
  const { t } = useTranslation(['homePageConfiguration', 'accountTypes']);
  const [activeItem, setActiveItem] = useState<any>(null);
  const theme = useTheme();

  const overrideBarOptions: ChartOptions<'bar'> = {
    plugins: {
      tooltip: {
        enabled: false,
        external: (data: any) => {
          const index = data.tooltip.dataPoints?.[0]?.parsed?.x;
          if (!isNull(index) && sData[index].key !== activeItem?.key) {
            setActiveItem(sData[index]);
          }
        },
      },
    },
  };

  const plugins: Plugin<'bar'>[] = [
    {
      id: 'tooltip',
      beforeEvent(_chart, args) {
        const { event } = args;
        if (event.type === 'mouseout') setActiveItem(null);
      },
    },
  ];

  return (
    <Box
      sx={{
        mt: 0.5,
      }}
    >
      {loading ? (
        <Skeleton width='100%' height='300px' variant='rounded' />
      ) : (
        <>
          <Typography
            data-testid='aum-explorer-bar-total-value'
            variant='bodyLarge'
            color='textSecondary'
            sx={{ fontWeight: 500 }}
          >
            {formatMoneyValue(totalValue, '$', 2, false)}
          </Typography>
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'row',
              justifyContent: 'center',
              gap: 1.5,
            }}
          >
            {sData.map((item, index) => (
              <Box
                key={item.key}
                data-testid={`aum-explorer-bar-item-${item.key}`}
                sx={{
                  display: 'flex',
                  flexDirection: 'row',
                  alignItems: 'center',
                  paddingY: 1.5,
                  paddingX: 1,
                  gap: 1,

                  ...(item.key === activeItem?.key
                    ? {
                        backgroundColor: theme.color.surfaceContainer,
                        borderRadius: theme.borderRadius.md,
                      }
                    : {
                        backgroundColor: 'none',
                      }),
                }}
              >
                <Box
                  sx={{
                    width: 8,
                    height: 8,
                    bgcolor: colors[index % colors.length],
                    borderRadius: 1,
                  }}
                />
                <Typography variant='labelLarge'>{t(item.key)}</Typography>
              </Box>
            ))}
          </Box>
          <Box
            sx={{
              width: '100%',
              height: '100%',
              minHeight: 230,
            }}
          >
            {sData.length > 0 ? (
              <BarChart
                labels={sData.map(
                  (item) =>
                    `${formatCompactNumber(item.value / 100, { isCurrency: true })} (${formatPercentValue(item.value / totalValue, 2)})`,
                )}
                datasets={[
                  {
                    label: '',
                    data: sData.map((item) => item.value),
                    backgroundColor: sData.map(
                      (_, index) => colors[index % colors.length],
                    ),
                    barPercentage: 1,
                    categoryPercentage: 0.98,
                  },
                ]}
                format='CENTS'
                yAxis={{
                  color: theme.color.onSurfaceVariant,
                  font: {
                    family: theme.font.base,
                    size: 12,
                    weight: '600',
                  },
                  position: 'right',
                  borderDisplay: false,
                }}
                xAxis={{
                  color: theme.color.onSurfaceVariant,
                  font: {
                    family: theme.font.base,
                    size: 12,
                    weight: '600',
                  },
                  borderDisplay: false,
                }}
                overrideOptions={overrideBarOptions}
                plugins={plugins}
              />
            ) : (
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  height: '100%',
                }}
              >
                <Typography variant='bodyLarge' sx={{ fontWeight: 600 }}>
                  {t('aumExplorer.noData')}
                </Typography>
              </Box>
            )}
          </Box>
        </>
      )}
    </Box>
  );
};
